<template>
  <v-card height="725">
    <v-card-title class="h6 px-8 d-flex flex-column align-start">
      {{ $t('routines.routine_template_step_title') }}
    </v-card-title>

    <div class="mt-4 mb-4 px-8">
      <div class="d-flex justify-space-between align-center mb-4">
        <SelectorFilter
          width="240px"
          :label="$t('routines.advanced_selector.filter_routine_by')"
          :options="routineDefinitionsPresetFiltersOptions"
          :value="presetFilter"
          @change="handleChangeFilter"
        />

        <span v-if="hasResults" class="caption blue-grey--text text--darken-1">
          {{ $t('routines.routine_total_templates', { total: totalResults }) }}
        </span>
      </div>
      <v-text-field
        class="routine-template-search-container body-1"
        data-private
        prepend-inner-icon="mdi-magnify"
        single-line
        outlined
        hide-details
        dense
        rounded
        background-color="white"
        full-width
        :loading="isSearching"
        :placeholder="`${$t('common.search')}...`"
        :value="searchTerms"
        @input="handleSearch"
      >
      </v-text-field>
    </div>

    <v-card-text class="px-8 pb-0">
      <div v-if="hasTemplates">
        <NewTemplateCard
          v-for="item in routineTemplates"
          class="mb-2"
          :key="item.template.id"
          :routine="item.template"
          :searchHighlights="item.highlights"
          :isSelected="selectedTemplateId === item.template.id"
          @showChecklist="showChecklist(item.template)"
          @selectTemplate="selectedTemplateId = item.template.id"
        />
      </div>

      <div v-else class="h-100">
        <div v-if="searchTerms">
          <p
            class="subtitle-2 font-weight-medium blue-grey--text text--darken-1 ma-0 pa-0"
            v-html="
              $sanitize(
                $t('routines.failed_routine_search', { terms: searchTerms }),
                { allowedTags: ['b'] }
              )
            "
          />
          <p
            class="subtitle-2 font-weight-medium blue-grey--text text--darken-1"
          >
            {{ $t('routines.failed_routine_search_try') }}
          </p>
        </div>
        <div
          v-if="!searchTerms && !isSearching"
          class="d-flex flex-column justify-center align-center h-100"
        >
          <EmptyState
            class="py-6 px-6"
            icon="mdi-bee"
            :iconSize="36"
            :title="$t('routines.routine_template_empty_state_title')"
            :message="$t('routines.routine_template_empty_state_description')"
          />
        </div>
      </div>
    </v-card-text>

    <div v-if="totalPages > 1" class="mb-2 pt-3 mx-8">
      <v-pagination
        total-visible="6"
        circle
        :disabled="isSearching"
        :length="totalPages"
        :value="currentPage"
        @input="handlePageChange"
      />
    </div>

    <v-card-actions class="justify-end px-8 pb-4">
      <Button
        class="me-2"
        plain
        color="grey"
        :text="$t('common.button.back')"
        @click="$emit('back')"
      />
      <Button
        color="primary"
        class="white--text"
        type="submit"
        :disabled="!hasSelectedTemplate"
        :text="$t('routines.routine_template_create')"
        @click="handleContinue"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin'
import routineDefinitionPermissions from '@/mixins/routineDefinitionPermissions.vue'

import _ from 'lodash'

import Button from '@/components/UI/Button.vue'
import NewTemplateCard from './NewTemplateCard.vue'
import SelectorFilter from '@/components/Filters/SelectorFilter.vue'
import EmptyState from '@/components/EmptyState.vue'

const ITEMS_PER_PAGE = 7

const CREATED_BY_ME = 'createdByMe'
const COLLABORATOR = 'collaborator'

export default {
  name: 'NewRoutineListDialog',

  mixins: [mixin, routineDefinitionPermissions],

  components: {
    Button,
    NewTemplateCard,
    SelectorFilter,
    EmptyState,
  },

  data() {
    return {
      routineTemplates: [],
      isSearching: false,
      currentPage: 1,
      totalPages: 0,
      searchTerms: '',
      selectedTemplateId: '',
      presetFilter: 'allRoutines',
      totalResults: 0,
    }
  },

  computed: {
    ...mapGetters([
      'organizationId',
      'siteId',
      'accountId',
      'organizationSites',
    ]),

    hasTemplates() {
      return !_.isEmpty(this.routineTemplates)
    },

    canCreateRoutines() {
      return this.getRoutineDefinitionPermissions({}).create
    },

    routineDefinitionsPresetFiltersOptions() {
      return _.compact([
        {
          text: this.$t(
            'routines.advanced_selector.all_routines_templates_option'
          ),
          value: 'allRoutines',
        },

        this.canCreateRoutines
          ? {
              text: this.$t('routines.advanced_selector.created_by_me_option'),
              value: 'createdByMe',
            }
          : null,

        {
          text: this.$t('routines.advanced_selector.im_collaborator_option'),
          value: 'collaborator',
        },
      ])
    },

    hasSelectedTemplate() {
      return this.selectedTemplateId
    },

    hasResults() {
      return this.totalResults > 0
    },

    defaultSearchHighlights() {
      return [this.searchTerms ?? '', ...(this.searchTerms?.split?.(' ') ?? [])]
    },
  },

  mounted() {
    this.fetchRoutineTemplates()
  },

  methods: {
    handleSearch: _.debounce(function(terms) {
      this.searchTerms = terms
      this.currentPage = 1
      this.fetchRoutineTemplates()
    }, 800),

    async fetchRoutineTemplates() {
      try {
        this.isSearching = true
        const params = {
          from: (this.currentPage - 1) * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
          terms: this.searchTerms,
          filters: { isGlobalTemplate: true },
          organizationId: this.organizationId,
          expansions: ['createdBy'],
        }

        if (this.presetFilter === CREATED_BY_ME) {
          params.filters.createdBy = this.accountId
        } else if (this.presetFilter === COLLABORATOR) {
          params.filters.collaborators = [this.accountId]
        }

        const response = await api.routine.getStreamRoutineDefinition(params)

        this.routineTemplates = response?.data?.results.map(
          routineDefinition => ({
            template: {
              ...routineDefinition.result,
              site: this.organizationSites?.find?.(site => {
                return (routineDefinition?.result?.site ?? '') === site.id
              }),
            },
            highlights:
              routineDefinition.highlights?.name ||
              this.defaultSearchHighlights,
          })
        )

        this.totalResults = response.data.totalRecordsInData
        this.totalPages =
          Math.ceil(response?.data?.totalRecordsInData / ITEMS_PER_PAGE) || 0

        return response.data.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSearching = false
      }
    },

    handlePageChange(page) {
      if (this.currentPage === page) return
      this.currentPage = page

      this.fetchRoutineTemplates()
    },

    searchRoutines(terms) {
      this.searchTerms = terms
      this.currentPage = 1
      return this.fetchRoutineTemplates()
    },

    handleContinue() {
      if (!this.selectedTemplateId) return
      this.navigateTo({
        name: 'createRoutineDefinition',
        params: {
          routineDefinitionId: this.selectedTemplateId,
          template: true,
        },
      })
    },

    showChecklist(data) {
      this.selectedTemplateId = data.id
      this.$emit('showChecklist', data)
    },

    handleChangeFilter(value) {
      this.presetFilter = value.value
      this.fetchRoutineTemplates()
    },
  },
}
</script>
<style lang="scss">
.routine-template-search-container {
  fieldset {
    border-color: $color-blue-grey-lighten1;
    height: 40px;
  }
  .v-input__control > .v-input__slot {
    padding: 0 16px;
    min-height: 36px !important;
  }
  .v-input__control,
  .v-text-field__slot {
    height: 36px;
  }
  .v-input__icon {
    height: 20px;
  }
  .v-progress-linear {
    width: 95%;
    margin-left: 2.5%;
  }
}
</style>
